// -----------------------------------------------------------------------------
//  useTablesStream
// -----------------------------------------------------------------------------
//  Feb 2026 — shared SSE subscription used by BOTH the desktop Restaurant page
//  (`Restaurant.jsx`) and the mobile waiter UI (`MobileRestaurant.jsx`) so that
//  every table mutation (order placed, items added, batch sent to kitchen /
//  fiscal, table cleared/paid, transfer) propagates to every open terminal
//  within ≈100 ms — no polling, no manual refresh required.
//
//  Ιούν 2026 — σταθεροποίηση: το effect τρέχει ΜΙΑ φορά ανά mount
//  (callbacks σε refs — παλιότερα το νέο identity του onBulkChange σε κάθε
//  render ξανάνοιγε το EventSource συνεχώς) και σε κάθε RECONNECT γίνεται
//  reconciliation refetch ώστε events που χάθηκαν στο κενό να μη χάνονται.
//
//  We can't use axios / fetch here because EventSource is a browser-native
//  primitive that doesn't accept custom headers. The JWT rides in the
//  ?token query string, exactly as the mobile client has been doing.
//
//  Usage:
//    useTablesStream({ setTables, onBulkChange: reloadTables });
// -----------------------------------------------------------------------------
import { useEffect, useRef } from "react";

// Merge server snapshot με τοπικό state διατηρώντας το τοπικό (αστάλτο)
// cart: αν τοπικά υπάρχουν open_items, ο server τα στέλνει κενά ΚΑΙ τα
// batches δεν αυξήθηκαν (δεν έγινε submit), κράτα το τοπικό cart.
export function mergeServerTables(prev, incoming) {
  return (incoming || []).map((srv) => {
    const loc = prev.find((t) => t.id === srv.id);
    if (!loc) return srv;
    const keep =
      (loc.open_items || []).length > 0 &&
      (srv.open_items || []).length === 0 &&
      (srv.submitted_batches || []).length <= (loc.submitted_batches || []).length;
    return keep ? { ...srv, open_items: loc.open_items } : srv;
  });
}

export function useTablesStream({ setTables, onBulkChange }) {
  const setTablesRef = useRef(setTables);
  const onBulkChangeRef = useRef(onBulkChange);
  setTablesRef.current = setTables;
  onBulkChangeRef.current = onBulkChange;

  useEffect(() => {
    const token =
      (typeof localStorage !== "undefined" &&
        localStorage.getItem("auth_token")) ||
      "";
    if (!token) return;
    const baseUrl = (process.env.REACT_APP_BACKEND_URL || "").replace(
      /\/$/,
      "",
    );
    const url = `${baseUrl}/api/tables/stream?token=${encodeURIComponent(token)}`;
    let es;
    try {
      es = new EventSource(url);
    } catch {
      return;
    }
    let opens = 0;
    es.onopen = () => {
      opens += 1;
      // Reconnect → όσα events δημοσιεύτηκαν στο κενό χάθηκαν· refetch.
      if (opens > 1) onBulkChangeRef.current?.();
    };
    es.onmessage = (ev) => {
      try {
        const m = JSON.parse(ev.data);
        if (!m || !m.type) return;
        // Deletion — remove the table from local state.
        if (m.deleted && m.table_id) {
          setTablesRef.current((prev) => prev.filter((t) => t.id !== m.table_id));
          return;
        }
        // Bulk change (set-count, import, force-reset) → caller re-fetches.
        if (m.type === "tables.refreshed") {
          onBulkChangeRef.current?.();
          return;
        }
        // Single-table mutation — merge in-place, or append when unseen.
        if (m.table) {
          setTablesRef.current((prev) => {
            const i = prev.findIndex((t) => t.id === m.table.id);
            if (i < 0) return [...prev, m.table];
            const next = [...prev];
            // Feb 2026 — the cart lives in local React state only
            // (see Restaurant.jsx `persistAsBatch`). If we have local
            // ``open_items`` and the incoming SSE snapshot would wipe
            // them (e.g. because the server-side copy is stale/empty),
            // KEEP the local cart. Any legitimate empty transition is
            // driven by ``persistAsBatch`` which already updates local
            // state directly.
            // Ιούν 2026: εξαίρεση — αν το snapshot έχει ΠΕΡΙΣΣΟΤΕΡΑ
            // submitted_batches από το τοπικό (μόλις έγινε submit), το
            // τοπικό cart έχει ήδη καταχωρηθεί ως batch → μην το κρατάς
            // (αλλιώς το σύνολο διπλασιάζεται).
            const localOpen = prev[i].open_items || [];
            const incomingOpen = m.table.open_items || [];
            const localBatches = (prev[i].submitted_batches || []).length;
            const incomingBatches = (m.table.submitted_batches || []).length;
            const shouldKeepLocalCart =
              localOpen.length > 0 &&
              incomingOpen.length === 0 &&
              incomingBatches <= localBatches;
            next[i] = {
              ...m.table,
              open_items: shouldKeepLocalCart ? localOpen : incomingOpen,
            };
            return next;
          });
        }
      } catch {
        /* malformed frame — silently drop */
      }
    };
    es.onerror = () => {
      // EventSource auto-reconnects; no action needed here.
    };
    return () => {
      try {
        es.close();
      } catch {
        /* noop */
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}
